import React, { useState, useMemo, useEffect } from 'react';
import ReactFlow, { Background, Controls, MiniMap } from 'reactflow';
import 'reactflow/dist/style.css';
import { createBacktest, runBacktest, updateStrategy } from '../../api';
import Button from '../../components/Button';
import {
  extractNumericParams,
  applyParamOverrides,
  nodeContext,
  nodeTitleFromParam,
  numericFieldLabel,
  titleCaseWords,
} from './utils';

const QUICK_EDIT_NODE_COLORS = ['#00d4aa', '#3b82f6', '#f59e0b', '#ef4444', '#14b8a6', '#8b5cf6', '#ec4899', '#22c55e'];

function quickNodeColor(nodeId) {
  const seed = String(nodeId || 'node');
  let hash = 0;
  for (let i = 0; i < seed.length; i += 1) {
    hash = (hash * 31 + seed.charCodeAt(i)) >>> 0;
  }
  return QUICK_EDIT_NODE_COLORS[hash % QUICK_EDIT_NODE_COLORS.length];
}

function quickGraphNodeTitle(node) {
  const base = node?.data?.label || titleCaseWords(node?.type || 'node');
  const context = nodeContext(node?.type, node?.data || {});
  return context ? `${base} (${context})` : base;
}

export default function QuickEditPanel({ backtest, strategyForEdit, strategyLoading, onStrategyUpdated, onRerunResult }) {
  const [quickValues, setQuickValues] = useState({});
  const [quickBusy, setQuickBusy] = useState(false);
  const [rerunning, setRerunning] = useState(false);
  const [quickError, setQuickError] = useState('');
  const [quickInfo, setQuickInfo] = useState('');
  const [activeQuickNodeId, setActiveQuickNodeId] = useState(null);
  const [quickGraphExpanded, setQuickGraphExpanded] = useState(false);

  const quickParams = useMemo(
    () => extractNumericParams(strategyForEdit?.graph),
    [strategyForEdit]
  );

  const quickParamGroups = useMemo(() => {
    const groups = {};
    quickParams.forEach((p) => {
      if (!groups[p.nodeId]) {
        groups[p.nodeId] = {
          nodeId: p.nodeId,
          nodeLabel: nodeTitleFromParam(p),
          nodeType: p.nodeType,
          params: [],
        };
      }
      groups[p.nodeId].params.push(p);
    });

    return Object.values(groups)
      .map((g) => ({
        ...g,
        params: g.params.sort((a, b) => numericFieldLabel(a).localeCompare(numericFieldLabel(b))),
      }))
      .sort((a, b) => a.nodeLabel.localeCompare(b.nodeLabel));
  }, [quickParams]);

  const quickGraphHighlightEdges = useMemo(() => {
    const set = new Set();
    if (!activeQuickNodeId) return set;
    (strategyForEdit?.graph?.edges || []).forEach((e) => {
      if (e.source === activeQuickNodeId || e.target === activeQuickNodeId) {
        set.add(e.id || `${e.source}:${e.target}`);
      }
    });
    return set;
  }, [strategyForEdit, activeQuickNodeId]);

  const quickGraphNodes = useMemo(() => {
    return (strategyForEdit?.graph?.nodes || []).map((n) => {
      const nodeColor = quickNodeColor(n.id);
      const isActive = activeQuickNodeId === n.id;
      return {
        id: n.id,
        type: 'default',
        position: n.position || { x: 0, y: 0 },
        data: { label: quickGraphNodeTitle(n) },
        draggable: false,
        selectable: true,
        style: {
          padding: 8,
          borderRadius: 8,
          border: `2px solid ${isActive ? nodeColor : 'var(--border)'}`,
          background: isActive ? `${nodeColor}22` : 'var(--card)',
          color: 'var(--text)',
          fontSize: 11,
          minWidth: 120,
          boxShadow: isActive ? `0 0 0 2px ${nodeColor}33` : 'none',
        },
      };
    });
  }, [strategyForEdit, activeQuickNodeId]);

  const quickGraphEdges = useMemo(() => {
    return (strategyForEdit?.graph?.edges || []).map((e) => {
      const edgeId = e.id || `${e.source}:${e.target}`;
      const isHighlighted = quickGraphHighlightEdges.has(edgeId);
      return {
        ...e,
        animated: isHighlighted,
        style: {
          stroke: isHighlighted ? 'var(--teal)' : 'var(--muted)',
          strokeWidth: isHighlighted ? 2.2 : 1.2,
        },
      };
    });
  }, [strategyForEdit, quickGraphHighlightEdges]);

  useEffect(() => {
    const map = {};
    quickParams.forEach((p) => { map[p.key] = p.value; });
    setQuickValues(map);
  }, [quickParams]);

  useEffect(() => {
    setActiveQuickNodeId(null);
  }, [strategyForEdit?.id]);

  const saveQuickEdits = async () => {
    if (!strategyForEdit) return null;
    const updatedGraph = applyParamOverrides(strategyForEdit.graph, quickParams, quickValues);
    if (!updatedGraph) return null;
    const payload = {
      name: strategyForEdit.name,
      description: strategyForEdit.description || '',
      graph: updatedGraph,
    };
    const resp = await updateStrategy(strategyForEdit.id, payload);
    onStrategyUpdated(resp.data);
    return resp.data;
  };

  const handleQuickSave = async () => {
    setQuickBusy(true);
    setQuickError('');
    setQuickInfo('');
    try {
      const saved = await saveQuickEdits();
      if (saved) {
        setQuickInfo('Quick edits saved to strategy.');
      }
    } catch (err) {
      setQuickError(err.response?.data?.detail || 'Failed to save quick edits.');
    } finally {
      setQuickBusy(false);
    }
  };

  const handleQuickSaveAndRerun = async () => {
    setQuickBusy(true);
    setRerunning(true);
    setQuickError('');
    setQuickInfo('');
    try {
      const saved = await saveQuickEdits();
      if (!saved) throw new Error('Could not save edited strategy.');

      const createResp = await createBacktest({
        strategy: saved.id,
        asset_type: backtest.asset_type,
        symbol: backtest.symbol,
        start_date: backtest.start_date,
        end_date: backtest.end_date,
        initial_cash: backtest.initial_cash,
        slippage_perc: backtest.slippage_perc,
        commission_perc: backtest.commission_perc,
        fill_on_close: backtest.fill_on_close,
        fx_interval: backtest.fx_interval,
      });

      const runResp = await runBacktest(createResp.data.id);
      if (onRerunResult) onRerunResult(runResp.data);
      setQuickInfo('Saved and launched rerun with updated parameters.');
    } catch (err) {
      setQuickError(err.response?.data?.detail || err.message || 'Failed to rerun with quick edits.');
    } finally {
      setQuickBusy(false);
      setRerunning(false);
    }
  };

  return (
    <div style={{
      marginBottom: 24,
      border: '1px solid var(--border)',
      borderRadius: 10,
      background: 'var(--card)',
      padding: 12,
    }}>
      <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 8, color: 'var(--text)' }}>
        Quick Edit Numeric Variables
      </div>
      <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 10 }}>
        Edit numeric node parameters grouped by node. Focus a value to highlight its node and connections in the strategy graph.
      </div>

      {!strategyLoading && quickParamGroups.length > 0 && (
        <div style={{ marginBottom: 12 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, gap: 8, flexWrap: 'wrap' }}>
            <div style={{ fontSize: 12, color: 'var(--muted)' }}>
              Strategy Graph Preview ({quickGraphNodes.length} nodes)
            </div>
            <button
              onClick={() => setQuickGraphExpanded((v) => !v)}
              style={{
                background: 'var(--surface)',
                color: 'var(--text)',
                border: '1px solid var(--border)',
                borderRadius: 6,
                padding: '4px 9px',
                fontSize: 11,
                cursor: 'pointer',
              }}
            >
              {quickGraphExpanded ? 'Collapse Graph' : 'Expand Graph'}
            </button>
          </div>

          <div style={{ border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden', height: quickGraphExpanded ? 320 : 180 }}>
            <ReactFlow
              nodes={quickGraphNodes}
              edges={quickGraphEdges}
              fitView
              minZoom={0.2}
              maxZoom={1.5}
              nodesDraggable={false}
              nodesConnectable={false}
              elementsSelectable
              proOptions={{ hideAttribution: true }}
              onNodeClick={(_, node) => setActiveQuickNodeId(node.id)}
              onPaneClick={() => setActiveQuickNodeId(null)}
            >
              <Background variant="dots" gap={16} size={1} color="#30363d" />
              <Controls showInteractive={false} position="top-right" />
              <MiniMap
                pannable
                zoomable
                style={{ background: '#161b22', width: quickGraphExpanded ? 120 : 90, height: quickGraphExpanded ? 70 : 55 }}
                nodeColor={(node) => quickNodeColor(node.id)}
              />
            </ReactFlow>
          </div>

          <div style={{ marginTop: 6, fontSize: 11, color: 'var(--muted)' }}>
            Click a node in the graph to highlight its quick-edit fields.
          </div>
        </div>
      )}

      {strategyLoading && <div style={{ color: 'var(--muted)', fontSize: 12 }}>Loading strategy…</div>}

      {!strategyLoading && quickParams.length === 0 && (
        <div style={{ color: 'var(--muted)', fontSize: 12 }}>No numeric parameters found in this strategy graph.</div>
      )}

      {!strategyLoading && quickParamGroups.length > 0 && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
          gap: 10,
        }}>
          {quickParamGroups.map((group) => {
            const nodeColor = quickNodeColor(group.nodeId);
            const isActiveNode = activeQuickNodeId === group.nodeId;
            return (
              <div
                key={group.nodeId}
                style={{
                  border: `1px solid ${isActiveNode ? nodeColor : 'var(--border)'}`,
                  borderRadius: 8,
                  padding: 10,
                  background: isActiveNode ? 'rgba(47, 214, 195, 0.08)' : 'var(--surface)',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 8 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                    <span
                      title={`Node ${group.nodeId}`}
                      style={{
                        width: 10,
                        height: 10,
                        borderRadius: '50%',
                        background: nodeColor,
                        flexShrink: 0,
                      }}
                    />
                    <span style={{ color: 'var(--text)', fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {group.nodeLabel}
                    </span>
                  </div>
                  <span style={{ color: 'var(--muted)', fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.4 }}>
                    {group.nodeType}
                  </span>
                </div>

                <div style={{ display: 'grid', gap: 8 }}>
                  {group.params.map((p) => (
                    <label key={p.key} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                      <span style={{ fontSize: 11, color: 'var(--muted)' }}>
                        {numericFieldLabel(p)}
                      </span>
                      <input
                        type="number"
                        value={quickValues[p.key] ?? p.value}
                        onFocus={() => setActiveQuickNodeId(group.nodeId)}
                        onBlur={() => setActiveQuickNodeId((prev) => (prev === group.nodeId ? null : prev))}
                        onChange={(e) => {
                          const v = e.target.value;
                          setQuickValues((prev) => ({ ...prev, [p.key]: v }));
                        }}
                        style={{
                          border: `1px solid ${isActiveNode ? nodeColor : 'var(--border)'}`,
                          borderRadius: 6,
                          background: 'var(--card)',
                          color: 'var(--text)',
                          padding: '6px 8px',
                          fontSize: 13,
                          fontFamily: 'var(--font-mono)',
                        }}
                      />
                    </label>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div style={{ display: 'flex', gap: 'var(--space-2)', marginTop: 'var(--space-3)', flexWrap: 'wrap' }}>
        <Button
          variant="secondary"
          loading={quickBusy && !rerunning}
          disabled={strategyLoading || quickParams.length === 0}
          onClick={handleQuickSave}
        >
          {quickBusy && !rerunning ? 'Saving…' : 'Save Quick Edits'}
        </Button>
        <Button
          variant="primary"
          loading={rerunning}
          disabled={quickBusy || strategyLoading || quickParams.length === 0}
          onClick={handleQuickSaveAndRerun}
        >
          {rerunning ? 'Saving + Running…' : 'Save + Re-run'}
        </Button>
      </div>

      {quickError && <div style={{ marginTop: 8, color: 'var(--red)', fontSize: 12 }}>{quickError}</div>}
      {quickInfo && <div style={{ marginTop: 8, color: 'var(--teal)', fontSize: 12 }}>{quickInfo}</div>}
    </div>
  );
}
